import {useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"

import {KepKeyReader} from "@/components/kep/KepKeyReader"
import {Alert, AlertDescription} from "@/components/ui/alert"
import {CardContent, CardDescription, CardHeader, CardTitle,} from "@/components/ui/card"
import {LoginFrame} from "@/components/login/LoginFrame"
import {createKepChallenge, verifyKep} from "@/lib/api/auth"
import {useSession} from "@/store/session"

export function KepLoginStep() {
  const location = useLocation()
  const navigate = useNavigate()
  const setSession = useSession((state) => state.setSession)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Lands on the documents list by default. A bounce from the bare `/` (the
  // placeholder workspace page) counts as "no specific destination" too.
  const requested = (location.state as { from?: { pathname?: string } } | null)
    ?.from?.pathname
  const from = requested && requested !== "/" ? requested : "/documents"

  return (
    <LoginFrame>
      <CardHeader className="text-center">
        <CardTitle>Вхід до АРМ «Суб'єкт ОТК»</CardTitle>
        <CardDescription>
          Зчитайте особистий ключ і підпишіть одноразовий запит для входу.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <KepKeyReader
          actionLabel="Увійти за допомогою КЕП"
          busy={isSubmitting}
          disabled={isSubmitting}
          onRead={async ({ adapter }) => {
            if (isSubmitting) return
            setIsSubmitting(true)
            setError(null)
            try {
              const challenge = await createKepChallenge()
              const kepVerification = await adapter.signWithData(
                new TextEncoder().encode(challenge)
              )
              const session = await verifyKep(kepVerification)
              setSession(session)

              // The backend now resolves and links the ЄДРТЗ subject during
              // `/kep/verify` itself (see `kep-auth-plugin.ts`), so a
              // successful response always carries a `subjectId` — there is
              // no separate manual "connect subject" step to route into
              // anymore.
              navigate(from, { replace: true })
            } catch (err) {
              const status = (err as { status?: number } | null)?.status
              setError(
                status === 401
                  ? "Вхід відхилено: не вдалося підтвердити підпис, або з цим сертифікатом не пов'язано жодного суб'єкта ОТК за даними ЄДРТЗ. Перевірте ключ і спробуйте ще раз."
                  : "Не вдалося підтвердити КЕП. Перевірте ключ і повторіть спробу."
              )
            } finally {
              setIsSubmitting(false)
            }
          }}
        />
      </CardContent>
    </LoginFrame>
  )
}
